// @ts-strict-ignore
import type {
  Action,
  BooleanRule,
  DataRoute,
  Expression,
  IndexedRule,
  Rules,
  Threshold,
  Attribute,
  ReasonCodeGroup,
  ServicesByName,
} from '../types';
import { getDeepMergeableExpressions, getDeepMergeableRules } from './helpers';
import { WorkflowImportUpdateResultType } from '../constants';

export function mergePendingRules({
  actions,
  thresholds,
  dataRoutes,
  jsonData,
  pendingRules,
  pendingExpressions,
  services,
  reasonCodeGroups,
}: {
  actions: Record<string, Action>;
  thresholds: Record<string, Threshold>;
  dataRoutes: Record<string, DataRoute>;
  jsonData: {
    actions: Record<string, Action>;
    thresholds: Record<string, Threshold>;
    dataRoutes: Record<string, DataRoute>;
    serviceAttributeData: Record<number, {
      service: string;
      attribute: Attribute;
    }>;
    reasonCodeGroupData: Record<number, ReasonCodeGroup>;
  };
  pendingRules: Record<string, Rules>;
  pendingExpressions: Record<string, Expression>;
  services: ServicesByName;
  reasonCodeGroups: ReasonCodeGroup[];
}) {
  const actionsWithUpdatedRules = [];
  Object.entries(pendingRules).forEach(([pendingRuleActionId, pendingRuleValue]) => {
    const existingAction = actions[pendingRuleActionId];
    // we extend the existing rule
    const mergeableRules = getDeepMergeableRules({
      incomingRule: pendingRuleValue,
      existingRule: existingAction.rules,
      actions,
      thresholds,
      services,
      reasonCodeGroups,
      serviceAttributeData: jsonData.serviceAttributeData,
      reasonCodeGroupData: jsonData.reasonCodeGroupData,
    });

    if (mergeableRules.length) {
      if ((existingAction.rules as IndexedRule).id) {
        // in this case there was only one threshold in the rule
        // so we just extend it as "and" operator combination
        // and let user change it later
        existingAction.rules = {
          and: [
            existingAction.rules,
            ...mergeableRules,
          ],
        } as BooleanRule;
      } else {
        const rules = existingAction.rules as BooleanRule;
        if (rules?.or) {
          rules.or = [
            ...rules.or,
            ...mergeableRules,
          ];
        }

        if (rules?.and) {
          rules.and = [
            ...rules.and,
            ...mergeableRules,
          ];
        }
      }

      actionsWithUpdatedRules.push({
        publicId: pendingRuleActionId,
        type: WorkflowImportUpdateResultType.RULE_UPDATED,
        manualResolution: false,
        data: mergeableRules,
      });
    }

    if (Object.values(existingAction.rules)?.length <= 0) {
      // nothing in the rule originally, so we just swap it out
      existingAction.rules = pendingRuleValue;
    }
  });

  Object.entries(pendingExpressions).forEach(([pendingExpActionId, pendingExpValue]) => {
    const existingAction = actions[pendingExpActionId];
    // we extend the existing expression
    const mergeableExpressions = getDeepMergeableExpressions({
      incomingExpression: pendingExpValue,
      existingExpression: existingAction.output.expression,
      actions,
      dataRoutes,
    });

    if (mergeableExpressions.length) {
      existingAction.output.expression = [
        ...existingAction.output.expression,
        '+',
        ...mergeableExpressions,
      ];

      actionsWithUpdatedRules.push({
        publicId: pendingExpActionId,
        type: WorkflowImportUpdateResultType.EXPRESSION_UPDATED,
        data: mergeableExpressions,
      });
    }

    if (Object.values(existingAction.output.expression)?.length <= 0) {
      // nothing in the expression originally, so we just swap it out
      existingAction.output.expression = pendingExpValue;
    }
  });

  return {
    actions,
    actionsWithUpdatedRules,
  };
}
